import React, { useEffect, useState, useCallback } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { usePushNotifications } from '../../hooks/usePushNotifications';

/**
 * Провайдер push уведомлений
 * Обрабатывает WebSocket подключение и показывает уведомления в интерфейсе
 */
const PushNotificationProvider = ({ children }) => {
  const [displayError, setDisplayError] = useState('');
  const {
    lastNotification,
    error,
    clearError,
    clearLastNotification,
    permissionGranted
  } = usePushNotifications();

  useEffect(() => {
    if (error) {
      setDisplayError(error);
    }
  }, [error]);

  const handleErrorClose = useCallback(() => {
    setDisplayError('');
    clearError();
  }, [clearError]);

  return (
    <>
      {children}
      
      {/* Snackbar для показа push уведомлений в интерфейсе */}
      <Snackbar
        open={Boolean(lastNotification)}
        autoHideDuration={6000}
        onClose={clearLastNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 8 }} // Отступ от навбара
      >
        <Alert
          severity="info"
          variant="filled"
          onClose={clearLastNotification}
          icon={<NotificationsIcon />}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            minWidth: 300,
            '& .MuiAlert-message': {
              width: '100%'
            }
          }}
        >
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {lastNotification?.title || 'Новое уведомление'}
            </Typography>
            <Typography variant="body2">
              {lastNotification?.body || 'Получено новое уведомление'}
            </Typography>
            {typeof Notification !== 'undefined' &&
              !permissionGranted &&
              lastNotification && (
              <Typography
                variant="caption"
                color="inherit"
                sx={{ opacity: 0.88, mt: 0.75, display: 'block', lineHeight: 1.35 }}
              >
                {Notification.permission === 'denied'
                  ? 'Всплывающие уведомления ОС отключены для сайта — включите в настройках браузера.'
                  : 'Чтобы дублировать уведомление вне вкладки, нажмите «Разрешить в браузере» в Настройках → Push.'}
              </Typography>
            )}
            {lastNotification?.data?.timestamp && (
              <Typography variant="caption" color="inherit" sx={{ opacity: 0.8, mt: 0.5, display: 'block' }}>
                {new Date(lastNotification.data.timestamp).toLocaleTimeString('ru-RU')}
              </Typography>
            )}
          </Box>
        </Alert>
      </Snackbar>

      {/* Snackbar для ошибок WebSocket */}
      <Snackbar
        open={Boolean(displayError)}
        autoHideDuration={8000}
        onClose={handleErrorClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          mt: 8,
          zIndex: (theme) => theme.zIndex.snackbar + 2
        }}
      >
        <Alert
          severity="warning"
          onClose={handleErrorClose}
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          sx={{
            minWidth: 220,
            maxWidth: 360,
            '& .MuiAlert-message': {
              py: 0.25
            }
          }}
        >
          <Typography variant="body2">
            {displayError}
          </Typography>
        </Alert>
      </Snackbar>


    </>
  );
};

export default PushNotificationProvider; 